import { Grid, InputLabel, styled }                                                    from '@mui/material';
import { FastField, FormikProps, useFormikContext }                               from 'formik';
import React, { FC, RefObject, useMemo, useRef }                                 from 'react';
import { FamilyMembersSelector }                                                 from '../../../formFields/FamilyMembersSelector';
import { PhoneInputField }                                                       from '../../../formFields/PhoneInputField';
import { SelectField }                                                           from '../../../formFields/SelectField';
import { TextInputField }                                                        from '../../../formFields/TextInputField';
import { WithCategorySelection }                                                 from '../../../hocs/withCategorySelection';
import { useFetchCategories }                                                    from '../../../queries/categories';
import { familyRoles }                                                           from '../../../queries/family';
import { UploadEntityFormLabel, UploadEntityFormRow, UploadEntityFormSpacing }   from '../../documents/styles';
import { GridDivider }                                                           from '../../layout/GridDivider';
import { ModalFooterActions }                                                    from '../shared/ModalFooterActions';
import { IContact }                                                              from './queries';
import { LoadingOverlay }                                                        from '../../LoadingOverlay';

interface IAddContactModalFormProps extends FormikProps<IContact> {
  familyId?: string;
  loading: boolean;
  formikRef: RefObject<FormikProps<IContact> | null>;
}

const Notes = styled(FastField)(({ theme }: any) => ({
  'textarea': {
    height: '0px !important',
  },
}));

export const UploadContactModalForm: FC<IAddContactModalFormProps> = ({
  familyId,
  loading,
  formikRef,
  ...form
}) => {
  const { values, setFieldValue } = form;
  const { data: categories, isLoading: loadingCategories } = useFetchCategories(familyId);

  const loadingRef   = useRef(loading);
  loadingRef.current = loading;

  const Form = useMemo(() => WithCategorySelection(function ContactFormInner({ categoriesDropdown }) {
    const { values: formValues, handleSubmit } = useFormikContext<IContact>();

    return (
      <>
        <UploadEntityFormSpacing onSubmit={handleSubmit}>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <FastField
                label={<>Title/Nickname <i>Optional</i></>}
                name={'title'}
                inputProps={{ maxLength: 100 }}
                component={TextInputField}
              />
            </Grid>

            <GridDivider margin="0 0 -10px" />

            <Grid
              item
              xs={12}
              sm={6}
            >
              <FastField
                label="First Name"
                name={'firstName'}
                inputProps={{ maxLength: 100 }}
                component={TextInputField}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
            >
              <FastField
                label="Last Name"
                name={'lastName'}
                inputProps={{ maxLength: 100 }}
                component={TextInputField}
              />
            </Grid>

            <GridDivider margin="0 0 -10px" />

            <Grid
              item
              xs={12}
              sm={6}
            >
              <InputLabel variant="outlined">Category</InputLabel>

              { categoriesDropdown }
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
            >
              <InputLabel variant="outlined">Role</InputLabel>

              <FastField
                name="role"
                options={familyRoles}
                component={SelectField}
              />
            </Grid>

            <GridDivider margin="0 0 -10px" />

            <Grid
              item
              xs={12}
            >
              <UploadEntityFormRow>
                <UploadEntityFormLabel width="auto">Tag <i>Optional</i></UploadEntityFormLabel>

                <FamilyMembersSelector
                  multiple
                  name="tagged"
                />
              </UploadEntityFormRow>
            </Grid>

            <GridDivider margin="0 0 -10px" />

            <Grid
              item
              xs={12}
            >
              <FastField
                fullWidth
                label={<>Office Address <i>Optional</i></>}
                name={'officeAddress'}
                inputProps={{ maxLength: 100 }}
                component={TextInputField}
              />

              <GridDivider margin="15px 0 -15px" />
            </Grid>

            <Grid
              item
              xs={12}
            >
              <FastField
                fullWidth
                label={<>Home Address <i>Optional</i></>}
                name={'homeAddress'}
                inputProps={{ maxLength: 100 }}
                component={TextInputField}
              />

              <GridDivider margin="15px 0 -15px" />
            </Grid>

            <Grid
              item
              xs={12}
            >
              <FastField
                fullWidth
                label={<>Email Address <i>Optional</i></>}
                name={'email'}
                inputProps={{ maxLength: 100 }}
                component={TextInputField}
              />

              <GridDivider margin="15px 0 -15px" />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <UploadEntityFormLabel width="auto">Office Number <i>Optional</i></UploadEntityFormLabel>
              <FastField
                fullWidth
                label="Office Number"
                name={'officeNumber'}
                component={PhoneInputField}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <UploadEntityFormLabel width="auto">Home/Cell Number <i>Optional</i></UploadEntityFormLabel>

              <FastField
                fullWidth
                label="Home/Cell Number"
                name={'homeNumber'}
                component={PhoneInputField}
              />
            </Grid>

            <Grid
              item
              xs={12}
            >
              <Notes
                multiline
                fullWidth
                rows={4}
                label={<>Notes <i>Optional</i></>}
                name={'notes'}
                component={TextInputField}
              />
            </Grid>

            <Grid
              item
              xs={12}
            >
              <ModalFooterActions
                formikRef={formikRef}
                disabled={loadingRef.current}
              >
                { loadingRef.current ? 'Uploading' : formValues?.id ? 'Edit' : 'Add' } Contact
              </ModalFooterActions>
            </Grid>
          </Grid>
        </UploadEntityFormSpacing>
        <LoadingOverlay loading={loadingRef.current} />
      </>
    );
  }), []);

  return useMemo(() => (
    <Form
      values={values}
      familyId={familyId}
      categories={categories}
      loading={loadingCategories}
      setFieldValue={setFieldValue}
    />
  ), [ loading, categories ]);
};
